import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan, faUnlink } from '@fortawesome/free-solid-svg-icons';
import { useAppStore } from '../../../store/useAppStore';
import { useBakerActionsEnabled } from '../../../hooks/useBakerActionsEnabled';
import { useMutationFeedback } from '../../../hooks/useMutationFeedback';
import { canDeleteOrder } from '../../../utils/orderUtils';
import Badge from '../../ui/Badge';
import IconButton from '../../ui/IconButton';
import ConfirmDialog from '../../ConfirmDialog';

function CardHeader({ order, index, count, onMove, halfCount = 0 }) {
  const { t } = useTranslation();
  const enabled = useBakerActionsEnabled();
  const joinSelection = useAppStore((state) => state.joinSelection);
  const toggleJoinSelection = useAppStore((state) => state.toggleJoinSelection);
  const joinOrders = useAppStore((state) => state.joinOrders);
  const deleteOrder = useAppStore((state) => state.deleteOrder);
  const disconnectOrder = useAppStore((state) => state.disconnectOrder);
  const { run } = useMutationFeedback();
  const [confirmAction, setConfirmAction] = useState(null);

  const isSelected = joinSelection.includes(order.id);
  // With exactly two queued halves the Select flow is replaced by a direct
  // join link in the card body (see BakerQueue's joinWith map).
  const twoQueuedHalves = halfCount === 2;
  const canJoin = order.half && !twoQueuedHalves && joinSelection.length >= 1 && !isSelected;
  const canDelete = canDeleteOrder(order);
  const canDisconnect = order.state === 'queued' && !!order.parts;

  const handleJoin = async () => {
    await run(() => joinOrders([...joinSelection, order.id]), t('msg.joined'));
  };

  const handleConfirm = async () => {
    const action = confirmAction;
    setConfirmAction(null);
    if (action === 'delete') {
      await run(() => deleteOrder(order.id));
    } else if (action === 'disconnect') {
      await run(() => disconnectOrder(order.id));
    }
  };

  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[color:var(--app-border)] px-4 py-3">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-base font-bold">
            {order.orderNo ? `#${order.orderNo} ` : ''}{order.customerName}
          </span>
          {order.half && (
            <Badge toneClass="border-amber-500/40 bg-amber-500/10 theme-accent-warm">{t('card.half')}</Badge>
          )}
          {order.parts && (
            <Badge toneClass="border-violet-500/40 bg-violet-500/10 text-violet-400">{t('card.parts', { count: order.parts.length })}</Badge>
          )}
        </div>
        <p className="mt-0.5 text-xs theme-muted">{new Date(order.createdAt).toLocaleString()}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {order.half && !twoQueuedHalves && (
          <button
            disabled={!enabled}
            className={`rounded-md border px-3 py-1.5 text-sm font-medium transition disabled:opacity-30 ${
              isSelected
                ? 'border-emerald-500/60 bg-emerald-500/10 theme-accent'
                : 'border-[color:var(--app-border)] hover:border-emerald-500/60'
            }`}
            onClick={() => toggleJoinSelection(order.id)}
          >
            {isSelected ? t('card.deselect') : t('card.select')}
          </button>
        )}
        {canJoin && (
          <button
            disabled={!enabled}
            className="rounded-md border border-emerald-500 bg-emerald-500 px-3 py-1.5 text-sm font-semibold text-zinc-950 transition hover:bg-emerald-400 disabled:opacity-30"
            onClick={handleJoin}
          >
            {t('card.join')}
          </button>
        )}
        {canDelete && (
          <IconButton
            icon={faTrashCan}
            label={t('card.delete')}
            disabled={!enabled}
            tone="red"
            onClick={() => setConfirmAction('delete')}
          />
        )}
        {canDisconnect && (
          <IconButton
            icon={faUnlink}
            label={t('card.disconnect')}
            disabled={!enabled}
            tone="amber"
            onClick={() => setConfirmAction('disconnect')}
          />
        )}
        <button
          className="rounded-md border border-[color:var(--app-border)] px-3 py-1.5 text-sm transition hover:border-emerald-500/60 disabled:opacity-30"
          onClick={() => onMove(order.id, -1)}
          disabled={!enabled || index === 0}
        >
          ↑
        </button>
        <button
          className="rounded-md border border-[color:var(--app-border)] px-3 py-1.5 text-sm transition hover:border-emerald-500/60 disabled:opacity-30"
          onClick={() => onMove(order.id, 1)}
          disabled={!enabled || index === count - 1}
        >
          ↓
        </button>
      </div>

      <ConfirmDialog
        open={confirmAction !== null}
        message={confirmAction === 'disconnect' ? t('confirm.disconnectMessage') : t('confirm.removeMessage')}
        confirmLabel={confirmAction === 'disconnect' ? t('card.disconnect') : t('confirm.remove')}
        cancelLabel={t('confirm.keep')}
        onConfirm={handleConfirm}
        onClose={() => setConfirmAction(null)}
      />
    </div>
  );
}

export default CardHeader;
